/**
 * AI 智能分类 · 文件夹列表请求（隔离接口）
 *
 * 与 src/actions/folder.js 的 fetchGroupFolders 参数/返回一致，
 * 但走独立 path /web/folder/ai/get，实现业务隔离。
 */

import { createSlice } from '@reduxjs/toolkit'
import fetchHelper from '../../utils/fetchHelper'
import { captureFetchException } from '../../utils/sentryUtils'
import { AppThunk } from '../../app/store'

declare const __API_SERVER_HOST__: string

interface FetchAIFoldersParams {
  groupID: number | string
  parentFolderID: number | string
}

/**
 * AI 文件夹原始项 · 透传给 adaptFolder 做字段适配。
 * 本层只声明调用方直接读取的 folderID，其余字段由 adaptFolder 在运行时消费。
 */
interface RawAIFolder {
  folderID: string | number
}

/** /web/folder/ai/get 业务响应体 · 仅声明本文件用到的字段 */
interface AIFoldersResponse {
  code: number
  data: {
    status: number
    /** 文件夹原始列表 · 字段适配交给 adaptFolder */
    data: RawAIFolder[]
    /**
     * 未归类（待归档）照片数 · 2026-05-28 新增。
     * 仅根请求（parentFolderID=0）下发；前端据此合成"待归档"虚拟文件夹。
     */
    unclassifiedPhotoCount?: number
  }
}

/** fetchAIFolders 解析结果 · 文件夹原始列表 + 未归类照片数 */
export interface AIFoldersResult {
  /** 文件夹原始列表（未 adapt）· 调用方再走 adaptFolderList */
  folders: RawAIFolder[]
  /** 未归类照片数 · 仅根请求有意义；子文件夹请求恒为 0 */
  unclassifiedPhotoCount: number
}

/**
 * 拉取 AI 智能分类文件夹列表（隔离接口 /web/folder/ai/get）。
 *
 * 由 FolderList（根 / 子文件夹）和详情页子文件夹加载共用。返回结果除文件夹
 * 列表外，还带根请求下发的 unclassifiedPhotoCount（未归类照片数）。
 *
 * @param data - groupID 团队 id；parentFolderID 父文件夹 id（0 为根）
 * @returns 成功 resolve {@link AIFoldersResult}；失败 catch 后 resolve undefined
 */
export function fetchAIFolders(
  data: FetchAIFoldersParams
): AppThunk<Promise<AIFoldersResult | undefined>> {
  return (dispatch, getState) => {
    const config = {
      url: __API_SERVER_HOST__ + '/web/folder/ai/get',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    }

    return fetchHelper(dispatch, getState, config)
      .then((res: AIFoldersResponse) => {
        if (res.code === 200 && res.data.status === 0) {
          // 边界处一次性归一：count 缺失 / 非数字统一兜底为 0
          const rawCount = Number(res.data.unclassifiedPhotoCount)
          return {
            folders: Array.isArray(res.data.data) ? res.data.data : [],
            unclassifiedPhotoCount: Number.isFinite(rawCount) ? rawCount : 0
          }
        }
        return Promise.reject(null)
      })
      .catch((err: any) => {
        captureFetchException(err, config)
        return undefined
      })
  }
}

/** /web/folder/ai/unclassified/photos 业务响应体 · 仅声明本文件用到的字段 */
interface UnclassifiedPhotosResponse {
  code: number
  data: {
    status: number
    /** 未分类照片总数 · 与接口 12 的 unclassifiedPhotoCount 一致 */
    totalCount?: number
    /** 游标（search_after）· 空串表示无后续 */
    lastPageCond?: string
    /** 照片原始列表 · FolderPhoto 结构，调用方用 adaptFolderPhoto 适配 */
    photos?: unknown[]
  }
}

/** fetchUnclassifiedPhotos 解析结果 · 照片原始列表 + 总数 + 下一页游标 */
export interface UnclassifiedPhotosResult {
  /** 照片原始列表（未 adapt）· 结构同 /web/folder/photo/get 的 FolderPhoto */
  photos: unknown[]
  /** 未分类照片总数 */
  totalCount: number
  /** 下一页游标 · 空串表示无后续 */
  lastPageCond: string
}

/** fetchUnclassifiedPhotos 入参 */
interface FetchUnclassifiedPhotosParams {
  groupID: number | string
  /** 每页条数 · 不传 / 0 由后端按默认 200 处理 */
  pageSize?: number
  /** 游标 · 首页传空串；后续页传上一页响应返回的 lastPageCond */
  lastPageCond?: string
}

/**
 * 拉取「待归档」（未分类）虚拟文件夹的照片列表（游标分页）。
 *
 * 与常规 /web/folder/photo/get 返回结构一致（FolderPhoto），但走游标
 * （lastPageCond / search_after）而非页码 —— 未分类可能上万张，ES from+size
 * 超 10000 会报错。由待归档详情页的 useUnclassifiedPhotos hook 调用。
 *
 * @param data - groupID 团队 id；pageSize 每页条数；lastPageCond 游标
 * @returns 成功 resolve {@link UnclassifiedPhotosResult}；失败 catch 后 resolve undefined
 */
export function fetchUnclassifiedPhotos(
  data: FetchUnclassifiedPhotosParams
): AppThunk<Promise<UnclassifiedPhotosResult | undefined>> {
  return (dispatch, getState) => {
    const config = {
      url: __API_SERVER_HOST__ + '/web/folder/ai/unclassified/photos',
      arguments: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    }

    return fetchHelper(dispatch, getState, config)
      .then((res: UnclassifiedPhotosResponse) => {
        if (res.code === 200 && res.data.status === 0) {
          const rawTotal = Number(res.data.totalCount)
          return {
            photos: Array.isArray(res.data.photos) ? res.data.photos : [],
            totalCount: Number.isFinite(rawTotal) ? rawTotal : 0,
            lastPageCond: res.data.lastPageCond ?? ''
          }
        }
        return Promise.reject(null)
      })
      .catch((err: any) => {
        captureFetchException(err, config)
        return undefined
      })
  }
}

const folderApiSlice = createSlice({
  name: 'aiClassify/folderApi',
  initialState: {},
  reducers: {}
})

export default folderApiSlice.reducer
